// Hideo - plan store: what Board, Calendar and Timeline share (src/features/ped.ts).
// - The saved plan chosen on this device (localStorage "creatorOS_pedview", per profile; read again at every call, like
//   the legacy code did) → activeView, projFilter (the plan's projects, or the active project), visible.
// - The filters (`filter`, reactive): Type (stored in "creatorOS_pedtype") and
//   format (not stored: reset when the plan or the project changes).
// - The "new format" field of the filter bar (PedFilters.vue).
// Choices redraw everything (app.renderAll, src/features/app.ts).
import { defineStore } from 'pinia'
import { reactive } from 'vue'
import { useDataStore } from './data'
import { newItemProject, useProjectsStore } from './projects'
import type { FormatName, PedView, Project, Video } from '../data/types'
import { PEDTYPE_KEY, PEDVIEW_KEY, filterVideos, inPedView, parseTypeFilter, resolvePedView, validSeriesFilter, visibleProjects, type PedFilter } from '../features/ped'
import { createFormat, nextSeriesColor } from '../features/formats'
import { saveAppData } from '../features/storage/save'
import { app } from '../features/app'
import { perProfileChoice } from './perProfileChoice'

function readType(): FormatName | '' {
  try { return parseTypeFilter(localStorage.getItem(PEDTYPE_KEY)) } catch { return '' }
}

export const usePedStore = defineStore('ped', () => {
  const data = useDataStore()
  const projects = useProjectsStore()
  const filter = reactive<PedFilter>({ series: '', type: readType() })
  const viewChoice = perProfileChoice(PEDVIEW_KEY)
  /** Bumped at every plan choice: computeds that read activeView() run again */
  const viewVer = viewChoice.ver

  /** The chosen plan of the active profile, or null (the active project) */
  function activeView(): PedView | null {
    void data.rev
    return resolvePedView(data.getData(), viewChoice.get(data.profiles.current))
  }
  /** Items of the open plan, or of the active project without one */
  function projFilter<T extends { projectId?: string }>(list: T[] | null | undefined): T[] {
    const v = activeView()
    return v ? inPedView(v, list) : projects.filter(list)
  }
  /** Videos shown with the plan or project and the Type and format filters (published ones included: the Board and
   *  the Timeline leave them out, the Calendar shows them) */
  function visible(videos: Video[]): Video[] {
    return filterVideos(data.getData(), projFilter(videos), filter)
  }
  /** Projects shown now (the plan's, or the active one, or all with "All projects") */
  function shownProjects(): Project[] {
    return visibleProjects(data.getData(), activeView(), projects.active())
  }

  /** Chooses a saved plan ('' = the active project): the format filter starts again */
  function setView(id: string): void {
    viewChoice.set(data.profiles.current, id)
    filter.series = ''
    app.renderAll()
  }
  /** Type filter: tapping the active Type (or "All") clears it; remembered on the device */
  function setType(type: string): void {
    filter.type = filter.type === type ? '' : parseTypeFilter(type)
    try { localStorage.setItem(PEDTYPE_KEY, filter.type) } catch { /* storage full or blocked */ }
    app.renderAll()
  }
  /** Format filter: tapping the active pill (or "All") clears it */
  function setSeries(f: string): void {
    filter.series = filter.series === f ? '' : f
    app.renderAll()
  }
  /** A format filter whose pill is gone (format deleted or renamed, another plan) goes back to "All" */
  function sync(): void {
    const v = validSeriesFilter(filter.series, projFilter(data.getData().series))
    if (v !== filter.series) filter.series = v
  }

  // ===== "New format" field of the filter bar =====
  const newFormat = reactive({ open: false, color: '', project: '' })
  function openNewFormat(): void {
    Object.assign(newFormat, { open: true, color: nextSeriesColor(data.getData()), project: '' })
  }
  function closeNewFormat(): void { newFormat.open = false }
  /** Project of the new format: the one picked in the field (when several projects are shown), otherwise the project
   *  of new items if it is shown, otherwise the first project shown */
  function newFormatProject(): string {
    const ps = shownProjects(), item = newItemProject()
    if (ps.some(p => p.id === newFormat.project)) return newFormat.project
    return ps.some(p => p.id === item) ? item : ps[0]?.id || item
  }
  /** Creates the format (or keeps the existing one with that name) and closes the field */
  function confirmNewFormat(name: string): void {
    newFormat.open = false
    const d = data.getData(), r = createFormat(d, name, newFormat.color, newFormatProject())
    if (r.created) saveAppData(d, true)
  }

  return { filter, viewVer, activeView, projFilter, visible, shownProjects, setView, setType, setSeries, sync,
    newFormat, openNewFormat, closeNewFormat, newFormatProject, confirmNewFormat }
})
